import { createFileRoute } from "@tanstack/react-router";
import { MinimalFooter } from "@/components/feo/MinimalFooter";
import { useMemo, useState } from "react";
import {
  ArrowRight, ArrowLeft, Sparkles, CheckCircle2, Trophy,
  Bone, HeartPulse, Dumbbell, Users2, Activity, Brain,
} from "lucide-react";
import { Button } from "@/components/ui/button";

import posOrtopedia from "@/assets/pos/thumb-ortopedia.webp";
import posDor from "@/assets/pos/thumb-dor.webp";
import posEsporte from "@/assets/pos/thumb-esporte.webp";
import posGeronto from "@/assets/pos/thumb-geronto.webp";
import posTraumato from "@/assets/pos/thumb-traumato.webp";
import posPrescricao from "@/assets/pos/thumb-prescricao.webp";

import { QuizzQuestionCard } from "@/components/quizz/QuizzQuestionCard";
import { QuizzContactStep } from "@/components/quizz/QuizzContactStep";
import { usePosQuizContent } from "@/lib/quizz/use-quizz-content";
import {
  estimateQualifierTotal,
  nextQualifierQuestion,
  type QualifierAnswer,
} from "@/lib/leads/qualifier-questions";




export const Route = createFileRoute("/quizz-pos-todas")({
  head: () => ({
    meta: [
      { title: "Quiz Pós ideal" },
      {
        name: "description",
        content:
          "Responda 8 perguntas rápidas e descubra qual das 6 pós-graduações da FEO mais combina com a próxima fase da sua carreira na fisioterapia.",
      },
      { name: "robots", content: "noindex, nofollow" },
      { property: "og:title", content: "Quiz Pós ideal" },
      {
        property: "og:description",
        content: "Teste de personalidade do fisioterapeuta, 8 perguntas, sua pós ideal em segundos.",
      },
    ],
  }),
  component: TestePage,
});

// ---------- Tipos & dados ----------

type PosSlug = "ortopedia" | "dor" | "esporte" | "geronto" | "traumato" | "prescricao";

type Pos = {
  slug: PosSlug;
  name: string;
  short: string;
  image: string;
  url: string;
  bullets: string[];
  Icon: typeof Bone;
};

const POS: Record<PosSlug, Pos> = {
  ortopedia: {
    slug: "ortopedia",
    name: "Fisioterapia em Ortopedia e Trauma",
    short: "Ortopedia & Trauma",
    image: posOrtopedia,
    url: "https://pos-orto.fisioemortopedia.com.br/",
    Icon: Bone,
    bullets: [
      "Avaliação e tratamento ortopédico completo",
      "Casos cirúrgicos e conservadores",
      "Raciocínio clínico baseado em evidências",
    ],
  },
  dor: {
    slug: "dor",
    name: "Especialização em Dor",
    short: "Dor",
    image: posDor,
    url: "https://www.pos-dor.fisioemortopedia.com.br/",
    Icon: Brain,
    bullets: [
      "Neurofisiologia da dor",
      "Dor crônica e persistente",
      "Educação em dor e abordagem biopsicossocial",
    ],
  },
  esporte: {
    slug: "esporte",
    name: "Fisioterapia em Esporte",
    short: "Esporte",
    image: posEsporte,
    url: "https://www.pos-esporte.fisioemortopedia.com.br/",
    Icon: Dumbbell,
    bullets: [
      "Atletas amadores e profissionais",
      "Performance e retorno ao esporte",
      "Prevenção de lesões",
    ],
  },
  geronto: {
    slug: "geronto",
    name: "Fisioterapia em Gerontologia",
    short: "Gerontologia",
    image: posGeronto,
    url: "https://pos-geronto.fisioemortopedia.com.br/",
    Icon: Users2,
    bullets: [
      "Saúde do idoso e envelhecimento ativo",
      "Funcionalidade e prevenção de quedas",
      "Doenças crônicas e comorbidades",
    ],
  },
  traumato: {
    slug: "traumato",
    name: "Fisioterapia em Traumatologia",
    short: "Traumatologia",
    image: posTraumato,
    url: "https://pos-traumato.fisioemortopedia.com.br/",
    Icon: HeartPulse,
    bullets: [
      "Pós-operatório e reabilitação avançada",
      "Fraturas, lesões e trauma esportivo",
      "Protocolos atualizados de reabilitação",
    ],
  },
  prescricao: {
    slug: "prescricao",
    name: "Fisioterapia na Prescrição de Exercícios Terapêuticos",
    short: "Prescrição de Exercícios",
    image: posPrescricao,
    url: "https://pos-prescricao.fisioemortopedia.com.br/",
    Icon: Activity,
    bullets: [
      "Carga, dose e progressão de exercício",
      "Reabilitação por exercício terapêutico",
      "Prescrição individualizada e baseada em evidências",
    ],
  },
};

const POS_LIST: Pos[] = Object.values(POS);

// ---------- Quiz ----------
// As perguntas do quiz são editáveis no admin (/admin/quizz) e chegam via
// usePosQuizContent(). Elas geram APENAS a recomendação de pós.


// ---------- Componente ----------
//
// Fluxo: intro → bloco 1 (qualificação) + bloco 2 (quiz), sem divisão visível
// → bloco final (nome/e-mail/telefone) → resultado.
// A captura/qualificação (RD Station + CRM) usa APENAS o bloco 1.

type Phase = "intro" | "questions" | "lead" | "result";

function TestePage() {
  const content = usePosQuizContent();
  const QUESTIONS = content.questions;

  const [phase, setPhase] = useState<Phase>("intro");
  const [qualAnswers, setQualAnswers] = useState<QualifierAnswer[]>([]);
  const [answers, setAnswers] = useState<number[]>([]);
  const [currentQ, setCurrentQ] = useState(0);

  const qualQuestion = nextQualifierQuestion(qualAnswers);
  const inQualBlock = qualQuestion !== null;
  const totalSteps = estimateQualifierTotal(qualAnswers) + QUESTIONS.length;
  const stepIndex = qualAnswers.length + (inQualBlock ? 0 : currentQ);

  const scores = useMemo(() => {
    const totals: Record<PosSlug, number> = {
      ortopedia: 0, dor: 0, esporte: 0, geronto: 0, traumato: 0, prescricao: 0,
    };
    answers.forEach((optIdx, qIdx) => {
      const opt = QUESTIONS[qIdx]?.options[optIdx];
      if (!opt) return;
      for (const [slug, w] of Object.entries(opt.weights) as [PosSlug, number][]) {
        if (slug in totals) totals[slug] += w;
      }
    });
    return totals;
  }, [answers, QUESTIONS]);

  const ranking = useMemo(() => {
    return (Object.entries(scores) as [PosSlug, number][])
      .sort((a, b) => b[1] - a[1])
      .map(([slug, score]) => ({ slug, score, pos: POS[slug] }));
  }, [scores]);

  const winner = ranking[0];
  const runnerUps = ranking.slice(1, 3);

  function startQuiz() {
    setQualAnswers([]);
    setAnswers([]);
    setCurrentQ(0);
    setPhase("questions");
  }

  function pickQualOption(optIdx: number) {
    if (!qualQuestion) return;
    const opt = qualQuestion.options[optIdx];
    setQualAnswers([
      ...qualAnswers,
      {
        pergunta: qualQuestion.pergunta,
        resposta: opt.label,
        pontos: opt.pontos,
        dealBreaker: !!opt.dealBreaker,
      },
    ]);
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function pickOption(optIdx: number) {
    const next = [...answers];
    next[currentQ] = optIdx;
    setAnswers(next);
    if (currentQ < QUESTIONS.length - 1) {
      setCurrentQ((c) => c + 1);
      if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      setPhase("lead");
      if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }

  function goBack() {
    if (!inQualBlock && currentQ > 0) {
      setCurrentQ((c) => c - 1);
      return;
    }
    if (qualAnswers.length > 0) {
      setQualAnswers(qualAnswers.slice(0, -1));
      setCurrentQ(0);
      return;
    }
    setPhase("intro");
  }

  return (
    <main className="flex min-h-screen flex-col bg-background">
      <div className="flex-grow">
        {phase === "intro" && <IntroPhase onStart={startQuiz} total={QUESTIONS.length} />}

        {phase === "questions" && inQualBlock && qualQuestion && (
          <QuizzQuestionCard
            title={qualQuestion.pergunta}
            options={qualQuestion.options.map((o) => o.label)}
            onPick={pickQualOption}
            onBack={goBack}
            index={stepIndex}
            total={totalSteps}
            instant
          />
        )}

        {phase === "questions" && !inQualBlock && QUESTIONS[currentQ] && (
          <QuizzQuestionCard
            title={QUESTIONS[currentQ].title}
            subtitle={QUESTIONS[currentQ].subtitle}
            options={QUESTIONS[currentQ].options.map((o) => o.label)}
            selected={answers[currentQ]}
            onPick={pickOption}
            onBack={goBack}
            index={stepIndex}
            total={totalSteps}
            instant
          />
        )}

        {phase === "lead" && winner && (
          <LeadPhase
            winner={winner}
            qualAnswers={qualAnswers}
            onSuccess={() => {
              void import("@/lib/black2x1/entregas-redirect").then((m) => m.goToBlackEntregas("quizz-pos"));
            }}
            onBack={() => {
              setPhase("questions");
              setCurrentQ(QUESTIONS.length - 1);
            }}
          />
        )}
        {phase === "result" && winner && <ResultPhase winner={winner} runnerUps={runnerUps} onRestart={startQuiz} />}

        <CatalogSection />
      </div>

      <MinimalFooter />
    </main>
  );
}



// ---------- Sub-componentes ----------

function IntroPhase({ onStart, total }: { onStart: () => void; total: number }) {
  return (
    <section className="relative overflow-hidden bg-hero">
      <div className="absolute inset-0 grid-bg opacity-30" />
      <div className="container relative mx-auto max-w-3xl px-4 py-16 text-center sm:py-24">
        <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-gold">
          <Sparkles className="h-3.5 w-3.5" /> Teste exclusivo · 2 minutos
        </div>
        <h1 className="mt-6 text-balance text-3xl font-extrabold leading-tight text-foreground sm:text-5xl">
          Qual <span className="text-gradient-primary">pós-graduação</span> combina com a sua próxima fase como fisioterapeuta?
        </h1>
        <p className="mx-auto mt-5 max-w-xl text-balance text-base text-muted-foreground sm:text-lg">
          Responda {total} perguntas rápidas e descubra qual das nossas 6 pós-graduações mais combina com o fisioterapeuta que você quer ser.
        </p>
        <Button
          size="lg"
          onClick={onStart}
          className="mt-8 rounded-full bg-success px-8 text-primary-foreground hover:bg-success/90"
        >
          Começar o teste <ArrowRight className="h-4 w-4" />
        </Button>
        <p className="mt-4 text-xs text-muted-foreground">Leva menos de 2 minutos · 100% gratuito</p>
      </div>
    </section>
  );
}

function LeadPhase({
  winner,
  qualAnswers,
  onSuccess,
  onBack,
}: {
  winner: { slug: PosSlug; score: number; pos: Pos };
  qualAnswers: QualifierAnswer[];
  onSuccess: () => void;
  onBack: () => void;
}) {
  const target = {
    slug: winner.slug,
    posNome: winner.pos.name,
    posUrl: winner.pos.url,
    conversionIdentifier: "quizz-pos",
  };
  return (
    <section className="bg-deep py-12 sm:py-16">
      <div className="container mx-auto max-w-2xl px-4">
        <div className="rounded-2xl border border-gold/30 bg-card p-6 shadow-card sm:p-10">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-gold">
              <Trophy className="h-3.5 w-3.5" /> Resultado pronto
            </div>
            <h2 className="mt-5 text-balance text-2xl font-bold text-foreground sm:text-3xl">
              Falta só mais um passo!
            </h2>
            <p className="mt-3 text-sm text-muted-foreground">
              Preencha seus dados para liberar a sua <span className="text-gradient-primary">pós ideal</span>.
            </p>
          </div>

          <div className="mt-8">
            <QuizzContactStep
              target={target}
              endpoint="/api/public/leads/quizz-pos"
              sourceLabel="quizz-pos"
              qualifierAnswers={qualAnswers}
              ctaLabel="Ver minha pós recomendada"
              onSuccess={onSuccess}
            />
          </div>

          <div className="mt-6 text-center">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onBack}
              className="text-muted-foreground"
            >
              <ArrowLeft className="h-4 w-4" /> Revisar respostas do quiz
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}



function ResultPhase({
  winner, runnerUps, onRestart,
}: {
  winner: { slug: PosSlug; score: number; pos: Pos };
  runnerUps: { slug: PosSlug; score: number; pos: Pos }[];
  onRestart: () => void;
}) {
  const { pos } = winner;
  const Icon = pos.Icon;
  return (
    <section className="bg-deep py-14 sm:py-20">
      <div className="container mx-auto max-w-5xl px-4">
        <div className="text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-gold">
            <Sparkles className="h-3.5 w-3.5" /> Sua pós ideal
          </div>
          <h2 className="mt-5 text-balance text-3xl font-extrabold leading-tight text-foreground sm:text-5xl">
            A pós que mais combina com você é{" "}
            <span className="text-gradient-primary">{pos.short}</span>.
          </h2>
        </div>

        <div className="mt-10 overflow-hidden rounded-3xl border border-gold/30 bg-card shadow-card">
          <div className="grid grid-cols-1 gap-0 lg:grid-cols-[minmax(0,340px)_minmax(0,1fr)]">
            <div className="relative mx-auto w-full max-w-[320px] p-4 sm:max-w-[380px] sm:p-6 lg:mx-0 lg:max-w-none lg:p-6">
              <div className="relative aspect-[4/5] w-full overflow-hidden rounded-2xl bg-muted shadow-card">
                <img loading="lazy" decoding="async"
                  src={pos.image}
                  alt={pos.name}
                  className="absolute inset-0 h-full w-full object-cover"
                />
              </div>
            </div>
            <div className="flex flex-col p-6 pt-0 sm:p-10 sm:pt-0 lg:pt-10">
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-gradient-primary text-primary-foreground shadow-glow">
                  <Icon className="h-6 w-6" />
                </div>
                <p className="min-w-0 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Pós-graduação recomendada
                </p>
              </div>
              <h3 className="mt-4 text-balance text-2xl font-bold text-foreground sm:text-3xl">{pos.name}</h3>
              <ul className="mt-5 space-y-2">
                {pos.bullets.map((b) => (
                  <li key={b} className="flex items-start gap-2 text-sm text-foreground">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-success" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <Button
                  asChild
                  size="lg"
                  className="flex-1 rounded-full bg-success text-primary-foreground hover:bg-success/90"
                >
                  <a href={pos.url} target="_blank" rel="noopener noreferrer">
                    Conhecer a pós <ArrowRight className="h-4 w-4" />
                  </a>
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  onClick={onRestart}
                  className="flex-1 rounded-full border-border bg-transparent text-foreground hover:bg-secondary"
                >
                  Refazer o teste
                </Button>
              </div>
              <p className="mt-4 text-xs text-muted-foreground">
                Nosso time também vai entrar em contato para te ajudar a escolher e tirar dúvidas sobre a matrícula.
              </p>
            </div>
          </div>
        </div>


        {runnerUps.length > 0 && (
          <div className="mt-12">
            <p className="text-center text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Outras pós que também combinam com você
            </p>
            <div className="mx-auto mt-5 grid max-w-3xl gap-4 sm:grid-cols-2">
              {runnerUps.map((r) => (
                <a
                  key={r.slug}
                  href={r.pos.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center gap-4 rounded-2xl border border-border bg-card p-4 transition hover:border-primary/50"
                >
                  <img loading="lazy" decoding="async" src={r.pos.image} alt={r.pos.name} className="h-16 w-16 flex-shrink-0 rounded-xl object-cover" />
                  <div className="flex-1">
                    <p className="text-sm font-bold text-foreground">{r.pos.short}</p>
                    <p className="mt-0.5 text-xs text-muted-foreground line-clamp-2">{r.pos.name}</p>
                  </div>
                  <ArrowRight className="h-4 w-4 text-muted-foreground transition group-hover:translate-x-0.5 group-hover:text-primary" />
                </a>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

function CatalogSection() {
  return (
    <section id="catalogo" className="border-t border-border/40 bg-background py-14 sm:py-20">
      <div className="container mx-auto max-w-6xl px-4">
        <div className="mx-auto max-w-2xl text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-gold">
            Catálogo completo
          </span>
          <h2 className="mt-5 text-balance text-3xl font-extrabold tracking-tight sm:text-4xl">
            Conheça <span className="text-gradient-primary">as 6 pós-graduações</span> da FEO
          </h2>
          <p className="mt-3 text-muted-foreground">
            Já sabe qual quer fazer? Acesse direto a página da pós escolhida.
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 gap-8 px-6 sm:px-0 sm:gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {POS_LIST.map((p) => {
            const Icon = p.Icon;
            return (
              <article
                key={p.slug}
                className="group relative flex flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-card transition-transform duration-300 hover:-translate-y-1"
              >
                <div className="relative aspect-[4/5] overflow-hidden bg-muted">
                  <img
                    src={p.image}
                    alt={p.name}
                    loading="lazy"
                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>

                <div className="flex flex-1 flex-col p-5">
                  <div className="flex items-center gap-2">
                    <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
                      <Icon className="h-4 w-4" />
                    </div>
                    <h3 className="text-base font-bold text-foreground">{p.short}</h3>
                  </div>
                  <p className="mt-2 text-sm text-muted-foreground">{p.name}</p>
                  <Button
                    asChild
                    className="mt-5 rounded-full bg-success text-primary-foreground hover:bg-success/90"
                  >
                    <a href={p.url} target="_blank" rel="noopener noreferrer">
                      Ver página da pós <ArrowRight className="h-4 w-4" />
                    </a>
                  </Button>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
